import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Tag } from '@carbon/react';
import { formatVnDateTime, timeZoneLabel } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader, ErrorState, BlockSkeleton, EmptyState } from '../components/states';
import { Money, When } from '../components/format';
import { GRAIN_LABEL, PARTIAL_NOTE, PERIOD_WORD, periodTick, type Grain } from '../components/Analytics';
import { Alerts, Columns, InsightBar, ScopeNote, ShareBar, Tile, fmt, grainOf, periodRow, secs, useInsightFilters, type Cmp, type Kind, type Scope } from '../components/Insights';

// Screen 3 of the KPI catalogue: game performance per game, category and live-casino provider, with the
// RTP tolerance band (forced rounds left out of the alarm), jackpots, pools, sessions and open lottery
// exposure; drill-down game -> daily / hourly -> players -> rounds (GET /api/admin/insights/games[/:game]).

type Head = { from: string; to: string; turnover: string; ggr: string; hold: number | null; rtp: number | null; bets: number; avgBet: number | null; bettors: number; ggrPerBettor: number | null };
type GameRow = {
  game: string; category: string; bet: string; win: string; refund: string; turnover: string; ggr: string; hold: number | null; rtp: number | null;
  rtpTheory: number | null; rtpLow: number | null; rtpHigh: number | null; rtpClean: number | null; rtpAlarm: boolean; forcedRounds: number; forcedKept: number; bets: number;
  avgBet: number | null; rounds: number | null; roundsWithStake: number | null; bettors: number; ggrPerBettor: number | null; share: number | null;
  jackpotIn: string; jackpotOut: string; jackpotWins: number; maxPayout: string | null; maxMultiple: number | null;
};
type TrendRow = { day: string; turnover: string; ggr: string; hold: number | null; rtp: number | null; bets: number; bettors: number; partial?: boolean };
type Games = {
  scope: Scope; headline: Head; compare: Cmp<Head>; trend: TrendRow[]; rows: GameRow[];
  categories: { category: string; turnover: string; ggr: string; hold: number | null; rtp: number | null; bets: number; avgBet: number | null; bettors: number; ggrPerBettor: number | null; share: number | null }[];
  vendors: { vendor: string; turnover: string; ggr: string; hold: number | null; bets: number; bettors: number; ggrPerBettor: number | null }[];
  jackpots: { wins: { game: string; jackpot: string; count: number; amount: string; max: string }[]; contributed: string; paid: string; liability: string; byDay: { day: string; total: string }[] };
  pools: { total: string; byDay: { day: string; total: string; partial?: boolean }[] };
  sessions: { bySystem: { he: string; sessions: number; closed: number; avgSec: number | null; players: number }[]; total: number; betsPerSession: number | null };
  lottery: { game: string; draw: string; tickets: number; stake: string; maxPayout: string; unrated: number; numbers: { number: string; tickets: number; exposure: string }[] }[];
  alerts: { kind: string; game: string; value: number; low: number; high: number }[];
  rtpZ: number;
};
type Detail = {
  scope: Scope; game: string; category: string; gids: string[];
  daily: TrendRow[];
  hourly: { hour: string; turnover: string; ggr: string; bets: number }[];
  players: { userId: number; nick: string | null; turnover: string; ggr: string; bets: number; days: number }[];
  rounds: { id: number; gid: string; round: string; openedAt: string; closedAt: string | null; status: string; stake: string; paid: string; refund: string; players: number; forced: boolean; result: string | null }[];
  roundsHidden: boolean;
};

export const CATEGORY: Record<string, string> = { table: 'Bàn', mini: 'Mini game', slot: 'Slot', crash: 'Crash', lottery: 'Xổ số', live: 'Live casino' };
const SYSTEM: Record<string, string> = { A: 'Hệ A (bàn, slot)', B: 'Hệ B (mini game)' };

const TILES: [keyof Head, string, Kind, string?][] = [
  ['turnover', 'Doanh số cược', 'money', 'Cược - hoàn cược'],
  ['ggr', 'Lãi nhà cái (GGR)', 'money'],
  ['hold', 'Tỷ lệ giữ lại', 'pct'],
  ['rtp', 'RTP thực tế', 'pct', 'Trả thưởng / doanh số'],
  ['bets', 'Số lần cược', 'count'],
  ['avgBet', 'Cược trung bình', 'money'],
  ['bettors', 'Người có cược', 'count'],
  ['ggrPerBettor', 'GGR mỗi người cược', 'money'],
];

const pct = (v: number | null) => fmt(v, 'pct');

function RtpCell({ r, z }: { r: GameRow; z: number }) {
  const band = r.rtpLow !== null && r.rtpHigh !== null ? `Dải bình thường ${pct(r.rtpLow)} đến ${pct(r.rtpHigh)} (z = ${z})` : 'Chưa có RTP lý thuyết';
  return (
    <td className="r num" data-label="RTP thực / lý thuyết" title={band + (r.forcedRounds ? `; bỏ ${r.forcedRounds} ván đặt kết quả: ${pct(r.rtpClean)}` : '')
      + (r.forcedKept ? `; ${r.forcedKept} ván đặt kết quả vẫn tính vì không chắc mọi người chơi của ván nằm trong báo cáo` : '')}>
      {pct(r.rtp)} <span className="muted">/ {pct(r.rtpTheory)}</span>
      {r.rtpAlarm ? <> <Tag type="red" size="sm">Ngoài dải</Tag></> : null}
    </td>
  );
}

// one row per day, week or month, newest first
function TrendTable({ rows, grain }: { rows: TrendRow[]; grain: Grain }) {
  return (
    <table className="plain">
      <thead><tr><th>{GRAIN_LABEL[grain]}</th><th className="r">Doanh số</th><th className="r">GGR</th><th className="r">Giữ lại</th><th className="r">RTP</th><th className="r">Lần cược</th><th className="r">Người cược</th></tr></thead>
      <tbody>{[...rows].reverse().map((x) => (
        <tr key={x.day}><td className="num is-title">{periodRow(x.day, grain, x.partial)}</td><td className="r" data-label="Doanh số"><Money v={x.turnover} /></td><td className="r" data-label="GGR"><Money v={x.ggr} signed /></td>
          <td className="r num" data-label="Giữ lại">{pct(x.hold)}</td><td className="r num" data-label="RTP">{pct(x.rtp)}</td><td className="r num" data-label="Lần cược">{x.bets}</td><td className="r num" data-label="Người cược">{x.bettors}</td></tr>
      ))}</tbody>
    </table>
  );
}

const trendNote = (grain: Grain) => `Người cược đếm mỗi tài khoản một lần trong mỗi ${PERIOD_WORD[grain]}.${grain !== 'day' ? ' ' + PARTIAL_NOTE : ''}`;

export function InsightGamesPage() {
  const f = useInsightFilters(['game', 'category']);
  const q = useQuery({ queryKey: ['insights', 'games', f.query], queryFn: () => api.get<Games>('/api/admin/insights/games', f.query) });
  const d = q.data;
  const grain = grainOf(f);
  const word = PERIOD_WORD[grain];
  const keep = new URLSearchParams(Object.entries(f.query).filter(([k]) => k !== 'game' && k !== 'category')).toString();
  const ggrMax = Math.max(0, ...(d?.rows ?? []).map((r) => Math.abs(Number(r.ggr))));
  const gameOptions = [['', 'Tất cả game'] as const, ...(d?.rows ?? []).map((r) => [r.game, r.game] as const)];
  if (f.values.game && !gameOptions.some(([g]) => g === f.values.game)) gameOptions.push([f.values.game, f.values.game]);
  const alerts = (d?.alerts ?? []).map((a) => ({ key: a.game, title: `${a.game}: RTP ngoài dải`,
    body: `RTP thực tế ${pct(a.value)} (đã bỏ ván đặt kết quả), dải bình thường ${pct(a.low)} đến ${pct(a.high)}.` }));
  return (
    <div className="page">
      <PageHeader title="Hiệu quả game" lead={`Theo game, nhóm game và nhà cung cấp live casino, ngày theo ${timeZoneLabel()}; tài khoản thử, nhân viên và bot không tính. RTP thực tế so với RTP lý thuyết ± z × σ / √(số lần cược); ván do nhà cái đặt kết quả không tính vào cảnh báo.`} />
      <InsightBar id="ig" f={f} exportPath="/api/admin/insights/games"
        selects={[
          { key: 'category', label: 'Nhóm game', options: [['', 'Tất cả nhóm'], ...Object.entries(CATEGORY)] },
          { key: 'game', label: 'Game', options: gameOptions },
        ]}
        exportSections={[['games', 'Theo game'], ['trend', `Theo ${word}`], ['categories', 'Theo nhóm'], ['vendors', 'Nhà cung cấp live'], ['lottery', 'Vé xổ số đang mở']]} />
      <ScopeNote scope={d?.scope} />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : d ? (
        <>
          <Alerts items={alerts} />
          <div className="kpis insight-kpis">
            {TILES.map(([k, label, kind, hint], i) => <Tile key={k} label={label} k={k} kind={kind} cur={d.headline} cmp={d.compare} hint={hint} lead={i === 1} />)}
          </div>
          <section className="section panel">
            <div className="panel__head"><h2>Lãi nhà cái theo {word}</h2></div>
            <Columns title={`Lãi nhà cái theo ${word}`} kind="money" height={200} labels={d.trend.map((x) => periodTick(x.day, grain, x.partial))}
              hints={d.trend.map((x) => `${periodRow(x.day, grain, x.partial)}, doanh số ${fmt(x.turnover, 'money')}, ${x.bets} lần cược, ${x.bettors} người`)}
              series={[{ name: 'Lãi nhà cái', values: d.trend.map((x) => Number(x.ggr)) }]} />
            <details className="insight-table-view">
              <summary>Xem bảng số theo {word}</summary>
              <div className="table-scroll"><TrendTable rows={d.trend} grain={grain} /></div>
            </details>
            <p className="muted">{trendNote(grain)}</p>
          </section>
          <section className="section panel">
            <div className="panel__head"><h2>Theo game</h2><span className="muted">Bấm tên game để xem theo {word}, giờ, người chơi và từng ván</span></div>
            {d.rows.length ? (
              <div className="table-scroll">
                <table className="plain">
                  <thead><tr><th>Game</th><th>Nhóm</th><th className="r">Doanh số</th><th>Lãi nhà cái</th><th className="r">Giữ lại</th><th className="r">RTP thực / lý thuyết</th><th className="r">Lần cược</th><th className="r">Cược TB</th><th className="r">Ván (có cược thật)</th><th className="r">Người cược</th><th className="r">GGR / người</th><th className="r">Góp hũ / trả hũ</th><th className="r">Thắng lớn nhất</th></tr></thead>
                  <tbody>{d.rows.map((r) => (
                    <tr key={r.game}>
                      <td className="is-title"><Link className="cell-link" to={`/insights/games/${encodeURIComponent(r.game)}?${keep}`}>{r.game}</Link></td>
                      <td data-label="Nhóm">{CATEGORY[r.category] ?? r.category}</td>
                      <td className="r" data-label="Doanh số"><Money v={r.turnover} /></td>
                      <td data-label="Lãi nhà cái"><ShareBar v={Number(r.ggr)} max={ggrMax} kind="money"><Money v={r.ggr} signed /></ShareBar></td>
                      <td className="r num" data-label="Giữ lại">{pct(r.hold)}</td>
                      <RtpCell r={r} z={d.rtpZ} />
                      <td className="r num" data-label="Lần cược">{r.bets.toLocaleString('vi-VN')}</td>
                      <td className="r" data-label="Cược TB"><Money v={r.avgBet === null ? null : Math.round(r.avgBet)} /></td>
                      <td className="r num" data-label="Ván">{r.rounds === null ? '-' : `${r.rounds} (${r.roundsWithStake})`}</td>
                      <td className="r num" data-label="Người cược">{r.bettors}</td>
                      <td className="r" data-label="GGR / người"><Money v={r.ggrPerBettor === null ? null : Math.round(r.ggrPerBettor)} signed /></td>
                      <td className="r" data-label="Góp hũ / trả hũ"><Money v={r.jackpotIn} /> <span className="muted">/</span> <Money v={r.jackpotOut} /></td>
                      <td className="r" data-label="Thắng lớn nhất"><Money v={r.maxPayout} />{r.maxMultiple !== null ? <span className="muted"> (x{fmt(r.maxMultiple, 'num')})</span> : null}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <EmptyState title="Không có game nào có cược trong khoảng này" body="Chọn khoảng ngày hoặc bộ lọc khác." />}
          </section>

          <div className="split split--even section">
            <section className="panel">
              <div className="panel__head"><h2>Theo nhóm game</h2></div>
              <table className="plain">
                <thead><tr><th>Nhóm</th><th className="r">Doanh số</th><th className="r">GGR</th><th className="r">Giữ lại</th><th className="r">RTP</th><th className="r">Người cược</th><th className="r">Tỷ trọng GGR</th></tr></thead>
                <tbody>{d.categories.map((c) => (
                  <tr key={c.category}>
                    <td className="is-title">{CATEGORY[c.category] ?? c.category}</td>
                    <td className="r" data-label="Doanh số"><Money v={c.turnover} /></td><td className="r" data-label="GGR"><Money v={c.ggr} signed /></td>
                    <td className="r num" data-label="Giữ lại">{pct(c.hold)}</td><td className="r num" data-label="RTP">{pct(c.rtp)}</td>
                    <td className="r num" data-label="Người cược">{c.bettors}</td><td className="r num" data-label="Tỷ trọng GGR">{pct(c.share)}</td>
                  </tr>
                ))}</tbody>
              </table>
              {d.vendors.length ? <>
                <h3 className="insight-sub">Live casino theo nhà cung cấp</h3>
                <table className="plain">
                  <thead><tr><th>Nhà cung cấp</th><th className="r">Doanh số</th><th className="r">GGR</th><th className="r">Giữ lại</th><th className="r">Lần cược</th><th className="r">Người cược</th></tr></thead>
                  <tbody>{d.vendors.map((v) => (
                    <tr key={v.vendor}><td className="is-title">{v.vendor}</td><td className="r" data-label="Doanh số"><Money v={v.turnover} /></td><td className="r" data-label="GGR"><Money v={v.ggr} signed /></td>
                      <td className="r num" data-label="Giữ lại">{pct(v.hold)}</td><td className="r num" data-label="Lần cược">{v.bets}</td><td className="r num" data-label="Người cược">{v.bettors}</td></tr>
                  ))}</tbody>
                </table>
              </> : null}
            </section>
            <section className="panel">
              <div className="panel__head"><h2>Hũ và quỹ</h2></div>
              <dl className="facts">
                <dt>Góp vào hũ trong khoảng</dt><dd><Money v={d.jackpots.contributed} unit /></dd>
                <dt>Trả hũ trong khoảng</dt><dd><Money v={d.jackpots.paid} unit /></dd>
                <dt>Hũ đang tích (hiện tại)</dt><dd><Money v={d.jackpots.liability} unit /></dd>
                <dt>Quỹ game (hiện tại)</dt><dd><Money v={d.pools.total} unit /></dd>
              </dl>
              {d.pools.byDay.some((x) => Number(x.total)) ? (
                <Columns title={`Quỹ game cuối mỗi ${word}`} kind="money" height={160} labels={d.pools.byDay.map((x) => periodTick(x.day, grain, x.partial))}
                  series={[{ name: 'Quỹ game', values: d.pools.byDay.map((x) => Number(x.total)) }]} />
              ) : null}
              {d.jackpots.wins.length ? (
                <table className="plain section">
                  <thead><tr><th>Game</th><th>Hũ</th><th className="r">Lần nổ</th><th className="r">Đã trả</th><th className="r">Lớn nhất</th></tr></thead>
                  <tbody>{d.jackpots.wins.map((w) => (
                    <tr key={w.game + w.jackpot}><td className="is-title">{w.game}</td><td data-label="Hũ">{w.jackpot}</td><td className="r num" data-label="Lần nổ">{w.count}</td>
                      <td className="r" data-label="Đã trả"><Money v={w.amount} /></td><td className="r" data-label="Lớn nhất"><Money v={w.max} /></td></tr>
                  ))}</tbody>
                </table>
              ) : null}
            </section>
          </div>

          <div className="split split--even section">
            <section className="panel">
              <div className="panel__head"><h2>Phiên kết nối</h2></div>
              <table className="plain">
                <thead><tr><th>Hệ</th><th className="r">Phiên</th><th className="r">Đã đóng</th><th className="r">Dài trung bình</th><th className="r">Người chơi</th></tr></thead>
                <tbody>{d.sessions.bySystem.map((s) => (
                  <tr key={s.he}><td className="is-title">{SYSTEM[s.he] ?? s.he}</td><td className="r num" data-label="Phiên">{s.sessions}</td><td className="r num" data-label="Đã đóng">{s.closed}</td>
                    <td className="r num" data-label="Dài trung bình">{secs(s.avgSec)}</td><td className="r num" data-label="Người chơi">{s.players}</td></tr>
                ))}</tbody>
              </table>
              <p className="muted">Số lần cược mỗi phiên: {fmt(d.sessions.betsPerSession, 'num')}.</p>
            </section>
            <section className="panel">
              <div className="panel__head"><h2>Vé xổ số đang mở (hiện tại)</h2></div>
              {d.lottery.length ? (
                <table className="plain">
                  <thead><tr><th>Loại</th><th>Kỳ</th><th className="r">Vé</th><th className="r">Tiền cược</th><th className="r">Trả tối đa nếu trúng</th><th>Số rủi ro nhất</th></tr></thead>
                  <tbody>{d.lottery.map((l) => (
                    <tr key={l.game + l.draw}>
                      <td className="is-title">{l.game}</td><td data-label="Kỳ">{l.draw}</td><td className="r num" data-label="Vé">{l.tickets}</td>
                      <td className="r" data-label="Tiền cược"><Money v={l.stake} /></td><td className="r" data-label="Trả tối đa"><Money v={l.maxPayout} /></td>
                      <td data-label="Số rủi ro nhất">{l.numbers.slice(0, 3).map((n) => `${n.number} (${fmt(n.exposure, 'money')})`).join(', ') || '-'}{l.unrated ? <span className="muted">, {l.unrated} vé chưa có tỷ lệ</span> : null}</td>
                    </tr>
                  ))}</tbody>
                </table>
              ) : <EmptyState title="Không có vé xổ số nào đang chờ quay" />}
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function InsightGameDetailPage() {
  const { can } = useAuth();
  const { game = '' } = useParams();
  const f = useInsightFilters();
  const path = `/api/admin/insights/games/${encodeURIComponent(game)}`;
  const q = useQuery({ queryKey: ['insights', 'game', game, f.query], queryFn: () => api.get<Detail>(path, f.query) });
  const d = q.data;
  const grain = grainOf(f);
  const word = PERIOD_WORD[grain];
  const back = new URLSearchParams(f.query).toString();
  return (
    <div className="page">
      <PageHeader title={`Game ${game}`} lead={<><Link to={`/insights/games?${back}`}>Hiệu quả game</Link> / theo {word}, theo giờ, người chơi và từng ván.</>} />
      <InsightBar id="igd" f={f} exportPath={path} exportSections={[['daily', `Theo ${word}`], ['players', 'Người chơi'], ['rounds', 'Từng ván']]} />
      <ScopeNote scope={d?.scope} />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : d ? (
        <>
          <p className="muted note">Nhóm {CATEGORY[d.category] ?? d.category}; mã trong game: {d.gids.join(', ')}.</p>
          <div className="split split--even section">
            <section className="panel">
              <div className="panel__head"><h2>Lãi nhà cái theo {word}</h2></div>
              <Columns title={`Lãi nhà cái ${game} theo ${word}`} kind="money" height={200} labels={d.daily.map((x) => periodTick(x.day, grain, x.partial))}
                hints={d.daily.map((x) => `${periodRow(x.day, grain, x.partial)}, doanh số ${fmt(x.turnover, 'money')}, ${x.bets} lần cược, ${x.bettors} người`)}
                series={[{ name: 'Lãi nhà cái', values: d.daily.map((x) => Number(x.ggr)) }]} />
            </section>
            <section className="panel">
              <div className="panel__head"><h2>Doanh số theo giờ</h2></div>
              {d.hourly.length ? (
                <Columns title={`Doanh số ${game} theo giờ`} kind="money" height={200}
                  labels={d.hourly.map((h) => { const t = formatVnDateTime(h.hour); return `${t.slice(11, 13)}h ${t.slice(0, 5)}`; })}
                  hints={d.hourly.map((h) => `${formatVnDateTime(h.hour)}, GGR ${fmt(h.ggr, 'money')}, ${h.bets} lần cược`)}
                  series={[{ name: 'Doanh số', values: d.hourly.map((h) => Number(h.turnover)) }]} />
              ) : <p className="muted">Theo giờ chỉ có khi xem toàn hệ thống (không lọc đại lý hay người đã nạp).</p>}
            </section>
          </div>
          <section className="section panel">
            <div className="panel__head"><h2>Theo {word}</h2></div>
            <div className="table-scroll"><TrendTable rows={d.daily} grain={grain} /></div>
            <p className="muted">{trendNote(grain)}</p>
          </section>
          <div className="split split--even section">
            <section className="panel">
              <div className="panel__head"><h2>Người chơi (doanh số cao nhất)</h2></div>
              {d.players.length ? (
                <table className="plain">
                  <thead><tr><th>Người chơi</th><th className="r">Doanh số</th><th className="r">Nhà cái lãi</th><th className="r">Lần cược</th><th className="r">Số ngày</th></tr></thead>
                  <tbody>{d.players.map((p) => (
                    <tr key={p.userId}>
                      <td className="is-title">{can('users.view') ? <Link className="cell-link" to={`/users/${p.userId}`}>{p.nick ?? '#' + p.userId}</Link> : p.nick ?? '#' + p.userId}</td>
                      <td className="r" data-label="Doanh số"><Money v={p.turnover} /></td><td className="r" data-label="Nhà cái lãi"><Money v={p.ggr} signed /></td>
                      <td className="r num" data-label="Lần cược">{p.bets}</td><td className="r num" data-label="Số ngày">{p.days}</td>
                    </tr>
                  ))}</tbody>
                </table>
              ) : <EmptyState title="Không có người chơi trong khoảng này" />}
            </section>
            <section className="panel">
              <div className="panel__head"><h2>Ván gần nhất</h2></div>
              {d.roundsHidden ? <EmptyState title="Ván không tách theo người chơi" body="Sổ ván chỉ ghi tổng tiền của cả ván. Bỏ lọc đại lý và lọc người đã nạp để xem ván." />
              : d.rounds.length ? (
                <table className="plain">
                  <thead><tr><th>Ván</th><th>Đóng lúc</th><th className="r">Cược thật</th><th className="r">Trả</th><th className="r">Hoàn</th><th className="r">Người</th></tr></thead>
                  <tbody>{d.rounds.map((r) => (
                    <tr key={r.id}>
                      <td className="is-title">{can('bets.view') ? <Link className="cell-link" to={`/bets?round=${encodeURIComponent(r.round)}`}>{r.round}</Link> : r.round}{r.forced ? <> <Tag type="purple" size="sm">Đặt kết quả</Tag></> : null}</td>
                      <td data-label="Đóng lúc"><When v={r.closedAt ?? r.openedAt} /></td>
                      <td className="r" data-label="Cược thật"><Money v={r.stake} /></td><td className="r" data-label="Trả"><Money v={r.paid} /></td>
                      <td className="r" data-label="Hoàn"><Money v={r.refund} /></td><td className="r num" data-label="Người">{r.players}</td>
                    </tr>
                  ))}</tbody>
                </table>
              ) : <EmptyState title="Game này không ghi ván" body="Slot và mini game quay từng lần không có ván chung; xem từng lần cược ở Lịch sử cược." />}
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
}
